'use client';

import { useMemo, useState, type ReactNode } from 'react';
import {
  Input,
  Select,
  MetaDefault,
  Checkbox,
  IconButton,
  FilterOptionHelpTooltip,
  createSelectListCollection,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUpDownIcon,
} from '@terra/ui-icons';
import type { ListCollection } from '@ark-ui/react';
import { useTranslations } from 'next-intl';
import {
  applyNestedSelectChange,
  findOptionLabel,
  getParentSelectionState,
  getSyntheticSelectedParentValues,
  sanitizeNestedSelectValues,
} from './multiSelectFilterSelection';

export type MultiSelectFilterOption = {
  label: string;
  value: string;
  tooltip?: string;
  children?: MultiSelectFilterOption[];
  isInitiallyExpanded?: boolean;
};

type SelectCollection = ListCollection<MultiSelectFilterOption>;

export type MultiSelectFilterProps = {
  collection: SelectCollection;
  values: string[];
  onValueChange: (values: string[]) => void;
  placeholder: string;
  minW?: string;
  maxHeight?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectAllEnabled?: boolean;
  resolveBadgeText?: (selectedValues: string[]) => string;
  disabled?: boolean;
  contentMinW?: string;
};

const flattenOptions = (
  items: MultiSelectFilterOption[],
): MultiSelectFilterOption[] =>
  items.flatMap((item) => [
    {
      label: item.label,
      value: item.value,
      tooltip: item.tooltip,
    },
    ...(item.children?.map((child) => ({
      label: child.label,
      value: child.value,
      tooltip: child.tooltip,
    })) ?? []),
  ]);

const getSelectableValues = (items: MultiSelectFilterOption[]): string[] =>
  items.flatMap((item) =>
    item.children?.length
      ? item.children.map((child) => child.value)
      : [item.value],
  );

const SelectionCheckbox = ({
  checked,
}: {
  checked: boolean | 'indeterminate';
}) => (
  <Box flexShrink={0} pointerEvents="none" as="span">
    <Checkbox checked={checked} size="sm" />
  </Box>
);

export function MultiSelectFilter({
  collection,
  values,
  onValueChange,
  placeholder,
  minW = '50px',
  maxHeight,
  searchable = false,
  searchPlaceholder,
  selectAllEnabled = false,
  resolveBadgeText,
  disabled,
  contentMinW,
}: MultiSelectFilterProps) {
  const tCommon = useTranslations('common');
  const tMenu = useTranslations('designSystem.menu');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedByValue, setExpandedByValue] = useState<
    Record<string, boolean>
  >({});
  const safeValues = useMemo(
    () => (Array.isArray(values) ? values : []),
    [values],
  );

  const topLevelItems = collection.items as MultiSelectFilterOption[];
  const hasNestedOptions = topLevelItems.some(
    (item) => (item.children?.length ?? 0) > 0,
  );

  const selectCollection = useMemo(() => {
    if (!hasNestedOptions) {
      return collection;
    }

    return createSelectListCollection({
      items: flattenOptions(topLevelItems),
    });
  }, [collection, hasNestedOptions, topLevelItems]);

  const selectValues = useMemo(() => {
    if (!hasNestedOptions) {
      return safeValues;
    }

    const syntheticParents = getSyntheticSelectedParentValues({
      selectedValues: safeValues,
      items: topLevelItems,
    });

    return syntheticParents.length > 0
      ? [...safeValues, ...syntheticParents]
      : safeValues;
  }, [hasNestedOptions, safeValues, topLevelItems]);

  const allSelectableValues = useMemo(
    () => getSelectableValues(topLevelItems),
    [topLevelItems],
  );

  const hasAllSelected = useMemo(
    () =>
      allSelectableValues.length > 0 &&
      allSelectableValues.every((value) => safeValues.includes(value)),
    [allSelectableValues, safeValues],
  );

  const displayText = useMemo(() => {
    if (safeValues.length === 0) {
      return placeholder;
    }

    if (resolveBadgeText) {
      return resolveBadgeText(safeValues);
    }

    if (safeValues.length === 1) {
      return (
        findOptionLabel({ items: topLevelItems, value: safeValues[0] }) ||
        safeValues[0]
      );
    }

    return tCommon('selectedCount', { count: safeValues.length });
  }, [safeValues, placeholder, resolveBadgeText, topLevelItems, tCommon]);

  const filteredItems = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return topLevelItems;
    }

    return topLevelItems.flatMap((item) => {
      const parentMatches = item.label
        .toLowerCase()
        .includes(normalizedSearchTerm);

      if (!item.children?.length) {
        return parentMatches ? [item] : [];
      }

      const matchingChildren = item.children.filter((child) =>
        child.label.toLowerCase().includes(normalizedSearchTerm),
      );

      if (parentMatches) {
        return [item];
      }

      if (matchingChildren.length > 0) {
        return [{ ...item, children: matchingChildren }];
      }

      return [];
    });
  }, [topLevelItems, searchTerm]);

  const isOptionExpanded = (option: MultiSelectFilterOption): boolean => {
    if (expandedByValue[option.value] !== undefined) {
      return expandedByValue[option.value];
    }

    if (searchTerm.trim()) {
      return true;
    }

    if (option.isInitiallyExpanded) {
      return true;
    }

    return (
      option.children?.some((child) => safeValues.includes(child.value)) ??
      false
    );
  };

  const toggleExpanded = (option: MultiSelectFilterOption) => {
    const nextExpanded = !isOptionExpanded(option);

    setExpandedByValue((current) => ({
      ...current,
      [option.value]: nextExpanded,
    }));
  };

  const renderOptionContent = ({
    label,
    tooltip,
    checked,
    expandControl,
  }: {
    label: string;
    tooltip?: string;
    checked: boolean | 'indeterminate';
    expandControl?: ReactNode;
  }) => (
    <HStack gap={2} flex="1" minW="0" w="full">
      <SelectionCheckbox checked={checked} />
      <HStack gap={2} minW="0">
        <Select.ItemText fontSize="xs">{label}</Select.ItemText>
        {tooltip && <FilterOptionHelpTooltip title={tooltip} iconSize={12} />}
      </HStack>
      {expandControl}
    </HStack>
  );

  return (
    <Select.Root
      collection={selectCollection}
      value={selectValues}
      onValueChange={(details) => {
        const next = Array.isArray(details.value) ? details.value : [];

        if (!hasNestedOptions) {
          onValueChange(next);

          return;
        }

        onValueChange(
          sanitizeNestedSelectValues({
            values: applyNestedSelectChange({
              previous: selectValues,
              next,
              items: topLevelItems,
            }),
            items: topLevelItems,
          }),
        );
      }}
      positioning={{ strategy: 'fixed' }}
      multiple
      disabled={disabled}
      onOpenChange={(details) => {
        if (!details.open) {
          setSearchTerm('');
        }
      }}
    >
      <Select.Control>
        <Select.Trigger minW={minW} maxW="200px" w="auto" h="6" fontSize="xs">
          <MetaDefault whiteSpace="nowrap" truncate>
            {displayText}
          </MetaDefault>
          <Select.Indicator>
            <ArrowUpDownIcon size={8} />
          </Select.Indicator>
        </Select.Trigger>
      </Select.Control>
      <Select.Positioner>
        <Select.Content
          py="1"
          maxHeight={maxHeight || undefined}
          overflowY={maxHeight ? 'auto' : undefined}
          minW={contentMinW}
        >
          <Select.ItemGroup>
            {searchable && (
              <Box
                px="2"
                py="2"
                borderBottomWidth="1px"
                borderColor="border.default"
              >
                <Input
                  size="xs"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={
                    searchPlaceholder ?? tCommon('searchPlaceholder')
                  }
                  h="7"
                />
              </Box>
            )}
            {selectAllEnabled && allSelectableValues.length > 0 && (
              <Box
                px="2"
                py="1.5"
                cursor="pointer"
                bg="bg.subtle"
                borderBottomWidth="1px"
                borderColor="border.default"
                _hover={{ bg: 'bg.muted' }}
                onClick={() =>
                  onValueChange(hasAllSelected ? [] : [...allSelectableValues])
                }
              >
                <MetaDefault fontWeight="medium" color="fg.default">
                  {hasAllSelected
                    ? tCommon('deselectAll')
                    : tCommon('selectAll')}
                </MetaDefault>
              </Box>
            )}
            {!selectAllEnabled && safeValues.length > 0 && (
              <Box
                px="2"
                py="1.5"
                cursor="pointer"
                bg="bg.subtle"
                borderBottomWidth="1px"
                borderColor="border.default"
                _hover={{ bg: 'bg.muted' }}
                onClick={() => onValueChange([])}
              >
                <MetaDefault fontWeight="medium" color="fg.default">
                  {tCommon('deselectAll')}
                </MetaDefault>
              </Box>
            )}
            {hasNestedOptions
              ? filteredItems.map((option) => {
                  const hasChildren = (option.children?.length ?? 0) > 0;

                  if (!hasChildren) {
                    return (
                      <Select.Item key={option.value} item={option} py="1">
                        {renderOptionContent({
                          label: option.label,
                          tooltip: option.tooltip,
                          checked: safeValues.includes(option.value),
                        })}
                      </Select.Item>
                    );
                  }

                  const parentSelected = getParentSelectionState({
                    item: option,
                    selectedValues: safeValues,
                  });
                  const expanded = isOptionExpanded(option);
                  const expandLabel = expanded
                    ? tMenu('collapse')
                    : tMenu('expand');

                  return (
                    <Box key={option.value}>
                      <Select.Item item={option} py="1">
                        {renderOptionContent({
                          label: option.label,
                          tooltip: option.tooltip,
                          checked: parentSelected,
                          expandControl: (
                            <IconButton
                              variant="ghost"
                              size="xs"
                              flexShrink={0}
                              stopPropagation
                              ml="auto"
                              aria-expanded={expanded}
                              aria-label={expandLabel}
                              onClick={() => toggleExpanded(option)}
                            >
                              {expanded ? (
                                <ArrowDown01Icon size={14} />
                              ) : (
                                <ArrowRight01Icon size={14} />
                              )}
                            </IconButton>
                          ),
                        })}
                      </Select.Item>
                      {expanded &&
                        option.children?.map((child) => (
                          <Select.Item
                            key={child.value}
                            item={child}
                            py="1"
                            pl="9"
                          >
                            {renderOptionContent({
                              label: child.label,
                              tooltip: child.tooltip,
                              checked: safeValues.includes(child.value),
                            })}
                          </Select.Item>
                        ))}
                    </Box>
                  );
                })
              : filteredItems.map((option) => (
                  <Select.Item key={option.value} item={option} py="1">
                    {renderOptionContent({
                      label: option.label,
                      tooltip: option.tooltip,
                      checked: safeValues.includes(option.value),
                    })}
                  </Select.Item>
                ))}
          </Select.ItemGroup>
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  );
}
